import { useTranslation } from 'react-i18next';
import {
  Text,
  Card,
  Flex,
  HStack,
  Circle,
  Divider,
  Box,
} from '@wegroup/design-system';
import styled from 'styled-components';
import CheckMark from '../../../../common/src/components/CheckMark';

export interface YellowhiveSalesConversationProductOption {
  id: string;
  titleKey: string;
  subtitleKey?: string;
  featureKeys: string[];
}

interface YellowhiveProductCardProps {
  option: YellowhiveSalesConversationProductOption;
  isSelected: boolean;
  onSelect: () => void;
}

const YellowhiveSalesConversationProductSelectionOption = ({
  option,
  isSelected,
  onSelect,
}: YellowhiveProductCardProps) => {
  const { t } = useTranslation();
  const title = t(option.titleKey);
  const subtitle = option.subtitleKey ? t(option.subtitleKey) : undefined;

  return (
    <CardWrapper
      checked={isSelected}
      onClick={onSelect}
      data-test-id="YellowhiveSalesConversationProductSelection_Card"
    >
      {/* Radio-style selection indicator */}
      <Circle
        size="2rem"
        border="2px solid"
        borderColor={isSelected ? 'primary.500' : 'gray.300'}
        bg={isSelected ? 'primary.500' : 'transparent'}
        position="absolute"
        top="1.6rem"
        right="1.6rem"
        display="flex"
        alignItems="center"
        justifyContent="center"
        data-test-id="YellowhiveSalesConversationProductSelection_RadioIndicator"
      >
        {isSelected && <Circle size="0.8rem" bg="white" />}
      </Circle>

      <Flex
        direction="column"
        align="flex-start"
        gap="1.6rem"
        w="100%"
        h="100%"
      >
        {/* Header – title and subtitle */}
        <Flex direction="column" align="flex-start" gap="0.8rem" w="100%">
          <Text textStyle="largeBodyBold">{title}</Text>
          {subtitle && (
            <Text textStyle="smallBody" color="gray.500">
              {subtitle}
            </Text>
          )}
        </Flex>

        {/* Subtle divider */}
        {subtitle && <Divider borderColor="gray.200" opacity={0.2} />}

        {/* Feature list */}
        <Flex direction="column" gap="0.8rem" w="100%">
          {option.featureKeys.map((featureKey, idx) => {
            const featureText = t(featureKey);
            // Skip only if translation is empty or explicitly returns the key (meaning translation doesn't exist)
            // Check if the returned text is the same as the key (translation missing)
            const isTranslationMissing = featureText === featureKey;
            if (!featureText || isTranslationMissing) return null;

            return (
              <HStack
                key={idx}
                align="flex-start"
                spacing="0.8rem"
                data-test-id="YellowhiveSalesConversationProductSelection_Feature"
              >
                <Box flexShrink={0}>
                  <CheckMark size="4" />
                </Box>
                <Text flex="1">{featureText}</Text>
              </HStack>
            );
          })}
        </Flex>
      </Flex>
    </CardWrapper>
  );
};

const CardWrapper = styled(Card)<{ checked?: boolean }>`
  position: relative;
  cursor: pointer;
  padding: 2.4rem;
  border: ${({ checked, theme }) =>
    checked ? `2px solid ${theme.primary[500]}` : '1px solid #E0E0E0'};
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  min-height: 100%;
  height: 100%;
  &:hover {
    background: #fafafa;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

export default YellowhiveSalesConversationProductSelectionOption;
